import {
    Button,
    Card,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridProps,
    DataGridRow,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    mergeClasses,
    SearchBox,
    Skeleton,
    SkeletonItem,
    TableColumnDefinition,
    Text,
    Toast,
    Toaster,
    ToastTitle,
    tokens,
    useId,
    useToastController,
} from '@fluentui/react-components';
import { AddRegular, ArrowClockwiseRegular, MailInboxRegular } from '@fluentui/react-icons';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientListingResponse, ClientsGetOrderEnum, ClientType } from '../../api';
import { useClientDirectory, useClientFacets } from '../../api/hooks/ClientsApi';
import { useBusinessUnitOptionsGet, useOfficeOptionsGet } from '../../api/hooks/OptionsApi';
import { generateDateLabel } from '../../helpers/generateDateLabel';
import { getClientTypeLabel } from '../../theme/ataTheme';
import { useDebouncedValue } from '../../utils/useDebouncedValue';
import BrandHeader from '../common/BrandHeader';
import ClientAvatar from '../common/ClientAvatar';
import ClientDetailDrawer from '../common/ClientDetailDrawer';
import ClientTypeBadge from '../common/ClientTypeBadge';
import CreatedByPersona from '../common/CreatedByPersona';
import DirectoryFilter, { DirectoryFilterOption } from '../common/DirectoryFilter';
import PaginationToolbar from '../common/PaginationToolbar';
import Container from '../layout/Container';

const useStyles = makeStyles({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        marginBottom: '14px',
    },
    search: {
        flex: 1,
        minWidth: '240px',
        maxWidth: '420px',
    },
    spacer: {
        flex: 1,
    },
    chips: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '14px',
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: '28px',
        padding: '0 6px 0 11px',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorBrandBackground2,
        border: `1px solid ${tokens.colorBrandStroke2}`,
        color: tokens.colorBrandForeground1,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
    },
    chipX: {
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        color: tokens.colorBrandForeground1,
        display: 'grid',
        placeItems: 'center',
        width: '18px',
        height: '18px',
        borderRadius: tokens.borderRadiusCircular,
        padding: 0,
        fontSize: '14px',
        lineHeight: 1,
        ':hover': { backgroundColor: tokens.colorBrandStroke2 },
    },
    card: {
        padding: 0,
        overflow: 'hidden',
    },
    tableWrap: {
        overflowX: 'auto',
    },
    cid: {
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: '13px',
        color: tokens.colorNeutralForeground2,
        fontVariantNumeric: 'tabular-nums',
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        minWidth: 0,
    },
    nameText: {
        fontWeight: tokens.fontWeightSemibold,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    row: {
        cursor: 'pointer',
    },
    rowSelected: {
        backgroundColor: tokens.colorBrandBackground2,
    },
    skeletonRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '14px 16px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '64px 24px',
        gap: '6px',
    },
    emptyIcon: {
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        display: 'grid',
        placeItems: 'center',
        marginBottom: '8px',
        fontSize: '26px',
    },
});

type SortDir = 'asc' | 'desc';
interface SortState {
    columnId: string;
    dir: SortDir;
}

interface Filters {
    clientTypes: string[];
    officeIds: string[];
    businessUnitIds: string[];
}

const EMPTY_FILTERS: Filters = { clientTypes: [], officeIds: [], businessUnitIds: [] };

const clientTypeFilterOptions: DirectoryFilterOption[] = Object.values(ClientType).map((value) => ({
    value,
    label: getClientTypeLabel(value),
}));

const columns: TableColumnDefinition<ClientListingResponse>[] = [
    createTableColumn<ClientListingResponse>({
        columnId: 'clientId',
        compare: () => 0, // server-sorted
        renderHeaderCell: () => 'Client ID',
        renderCell: (item) => (
            <span style={{ fontFamily: tokens.fontFamilyMonospace, fontWeight: tokens.fontWeightBold }}>
                {item.clientId}
            </span>
        ),
    }),
    createTableColumn<ClientListingResponse>({
        columnId: 'name',
        compare: () => 0,
        renderHeaderCell: () => 'Name',
        renderCell: (item) => <NameCell item={item} />,
    }),
    createTableColumn<ClientListingResponse>({
        columnId: 'clientType',
        compare: () => 0,
        renderHeaderCell: () => 'Type',
        renderCell: (item) => <ClientTypeBadge clientType={item.clientType} />,
    }),
    createTableColumn<ClientListingResponse>({
        columnId: 'officeName',
        compare: () => 0,
        renderHeaderCell: () => 'Office',
        renderCell: (item) => item.officeName,
    }),
    createTableColumn<ClientListingResponse>({
        columnId: 'businessUnitName',
        compare: () => 0,
        renderHeaderCell: () => 'Business Unit',
        renderCell: (item) => item.businessUnitName ?? '--',
    }),
    createTableColumn<ClientListingResponse>({
        columnId: 'createdAt',
        compare: () => 0,
        renderHeaderCell: () => 'Created',
        renderCell: (item) => generateDateLabel(item.createdAt),
    }),
    createTableColumn<ClientListingResponse>({
        columnId: 'createdBy',
        compare: () => 0,
        renderHeaderCell: () => 'Created By',
        renderCell: (item) => <CreatedByPersona name={item.createdBy} />,
    }),
];

// Constrain the Client ID column (narrow + mono) so it doesn't hog horizontal space.
const CLIENT_ID_SIZING: DataGridProps['columnSizingOptions'] = {
    clientId: { idealWidth: 130, minWidth: 100 },
};

const NameCell = ({ item }: { item: ClientListingResponse }) => {
    const styles = useStyles();
    return (
        <div className={styles.nameCell}>
            <ClientAvatar name={item.name} clientType={item.clientType} />
            <span className={styles.nameText}>{item.name}</span>
        </div>
    );
};

export const RecentClientsTable = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const toasterId = useId('directory-toaster');
    const { dispatchToast } = useToastController(toasterId);

    const [searchInput, setSearchInput] = React.useState('');
    const search = useDebouncedValue(searchInput, 250);
    const [sort, setSort] = React.useState<SortState>({ columnId: 'createdAt', dir: 'desc' });
    const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [refreshing, setRefreshing] = React.useState(false);
    const [selected, setSelected] = React.useState<ClientListingResponse | null>(null);
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const { data: officeOptions } = useOfficeOptionsGet();
    const { data: businessUnitOptions } = useBusinessUnitOptionsGet();

    const { data, isLoading, isError, error, refetch } = useClientDirectory({
        search: search || undefined,
        sort: sort.columnId,
        order: sort.dir === 'asc' ? ClientsGetOrderEnum.Asc : ClientsGetOrderEnum.Desc,
        page,
        pageSize,
        clientTypes: filters.clientTypes as ClientType[],
        officeIds: filters.officeIds,
        businessUnitIds: filters.businessUnitIds,
    });

    // Per-value facet counts (reflect the current search term).
    const { data: facets } = useClientFacets({ search: search || undefined });
    const countFor = React.useCallback(
        (facet: 'clientType' | 'office' | 'businessUnit', value: string) =>
            facets?.[facet]?.find((f) => f.value === value)?.count,
        [facets],
    );

    const items = data?.items ?? [];
    const total = data?.total;

    // Reset to first page whenever the result set changes shape.
    const resetPage = () => setPage(0);

    const clientTypeFilterOptionsWithCounts: DirectoryFilterOption[] = React.useMemo(
        () => clientTypeFilterOptions.map((o) => ({ ...o, count: countFor('clientType', o.value) })),
        [countFor],
    );
    const officeFilterOptions: DirectoryFilterOption[] = React.useMemo(
        () => (officeOptions ?? []).map((o) => ({ value: o.id, label: o.name, count: countFor('office', o.id) })),
        [officeOptions, countFor],
    );
    const businessUnitFilterOptions: DirectoryFilterOption[] = React.useMemo(
        () => (businessUnitOptions ?? []).map((b) => ({ value: b.id, label: b.name, count: countFor('businessUnit', b.id) })),
        [businessUnitOptions, countFor],
    );
    const officeLabel = (id: string) => officeFilterOptions.find((o) => o.value === id)?.label ?? id;
    const buLabel = (id: string) => businessUnitFilterOptions.find((b) => b.value === id)?.label ?? id;

    const activeChips = [
        ...filters.clientTypes.map((v) => ({ group: 'clientTypes' as const, value: v, label: `Type: ${getClientTypeLabel(v)}` })),
        ...filters.officeIds.map((v) => ({ group: 'officeIds' as const, value: v, label: `Office: ${officeLabel(v)}` })),
        ...filters.businessUnitIds.map((v) => ({ group: 'businessUnitIds' as const, value: v, label: `Business Unit: ${buLabel(v)}` })),
    ];

    const sortState: DataGridProps['sortState'] = {
        sortColumn: sort.columnId,
        sortDirection: sort.dir === 'asc' ? 'ascending' : 'descending',
    };

    const onSortChange: DataGridProps['onSortChange'] = (_e, next) => {
        setSort({
            columnId: String(next.sortColumn),
            dir: next.sortDirection === 'ascending' ? 'asc' : 'desc',
        });
        resetPage();
    };

    const setFilterGroup = (group: keyof Filters, values: string[]) => {
        setFilters((prev) => ({ ...prev, [group]: values }));
        resetPage();
    };

    const removeChip = (group: keyof Filters, value: string) => {
        setFilters((prev) => ({ ...prev, [group]: prev[group].filter((v) => v !== value) }));
        resetPage();
    };

    const clearAll = () => {
        setFilters(EMPTY_FILTERS);
        setSearchInput('');
        resetPage();
    };

    const handleRefresh = () => {
        setRefreshing(true);
        refetch().finally(() => {
            setRefreshing(false);
            dispatchToast(
                <Toast>
                    <ToastTitle>Client list refreshed</ToastTitle>
                </Toast>,
                { intent: 'success' },
            );
        });
    };

    const openDrawer = (client: ClientListingResponse) => {
        setSelected(client);
        setDrawerOpen(true);
    };

    const showSkeleton = isLoading || refreshing;
    const showEmpty = !showSkeleton && !isError && items.length === 0;

    return (
        <Container>
            <Toaster toasterId={toasterId} position="bottom" />

            <BrandHeader title="Client Directory">
                <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={handleRefresh}>
                    Refresh
                </Button>
                <Button appearance="primary" icon={<AddRegular />} onClick={() => navigate('/FormPage')}>
                    Create Client
                </Button>
            </BrandHeader>

            <div className={styles.toolbar}>
                <SearchBox
                    className={styles.search}
                    placeholder="Search by client name, ID, or office…"
                    value={searchInput}
                    onChange={(_e, d) => {
                        setSearchInput(d.value);
                        resetPage();
                    }}
                />
                <DirectoryFilter
                    name="clientType"
                    label="Client Type"
                    options={clientTypeFilterOptionsWithCounts}
                    selected={filters.clientTypes}
                    onChange={(v) => setFilterGroup('clientTypes', v)}
                />
                <DirectoryFilter
                    name="office"
                    label="Office"
                    options={officeFilterOptions}
                    selected={filters.officeIds}
                    onChange={(v) => setFilterGroup('officeIds', v)}
                />
                <DirectoryFilter
                    name="businessUnit"
                    label="Business Unit"
                    options={businessUnitFilterOptions}
                    selected={filters.businessUnitIds}
                    onChange={(v) => setFilterGroup('businessUnitIds', v)}
                />
                <span className={styles.spacer} />
            </div>

            {activeChips.length > 0 && (
                <div className={styles.chips}>
                    {activeChips.map((chip) => (
                        <span key={`${chip.group}:${chip.value}`} className={styles.chip}>
                            {chip.label}
                            <button
                                type="button"
                                className={styles.chipX}
                                aria-label={`Remove ${chip.label}`}
                                onClick={() => removeChip(chip.group, chip.value)}
                            >
                                ✕
                            </button>
                        </span>
                    ))}
                    {activeChips.length > 1 && (
                        <Button appearance="transparent" size="small" onClick={clearAll}>
                            Clear all
                        </Button>
                    )}
                </div>
            )}

            <Card className={styles.card}>
                {isError ? (
                    <MessageBar key="error" intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>Error Retrieving Clients - </MessageBarTitle>
                            {error?.toString()}
                        </MessageBarBody>
                    </MessageBar>
                ) : showSkeleton ? (
                    <Skeleton aria-label="Loading clients">
                        {Array.from({ length: pageSize }).map((_, i) => (
                            <div key={i} className={styles.skeletonRow}>
                                <SkeletonItem style={{ width: 64 }} />
                                <SkeletonItem shape="rectangle" size={32} style={{ width: 32, borderRadius: 8 }} />
                                <SkeletonItem style={{ width: 180 }} />
                                <SkeletonItem style={{ width: 90 }} />
                                <SkeletonItem style={{ width: 120 }} />
                                <SkeletonItem style={{ width: 90 }} />
                                <SkeletonItem style={{ width: 110 }} />
                            </div>
                        ))}
                    </Skeleton>
                ) : showEmpty ? (
                    <div className={styles.empty}>
                        <div className={styles.emptyIcon} aria-hidden>
                            <MailInboxRegular />
                        </div>
                        <Text size={400} weight="semibold">
                            No clients match your filters
                        </Text>
                        <Text>Try a different search term or clear some filters to see more results.</Text>
                        <Button style={{ marginTop: 10 }} onClick={clearAll}>
                            Clear all filters
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className={styles.tableWrap}>
                            <DataGrid
                                items={items}
                                columns={columns}
                                sortable
                                sortState={sortState}
                                onSortChange={onSortChange}
                                columnSizingOptions={CLIENT_ID_SIZING}
                                getRowId={(item) => item.id}
                            >
                                <DataGridHeader>
                                    <DataGridRow>
                                        {({ renderHeaderCell }) => (
                                            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                                        )}
                                    </DataGridRow>
                                </DataGridHeader>
                                <DataGridBody<ClientListingResponse>>
                                    {({ item, rowId }) => (
                                        <DataGridRow<ClientListingResponse>
                                            key={rowId}
                                            className={mergeClasses(
                                                styles.row,
                                                selected?.id === item.id && styles.rowSelected,
                                            )}
                                            onClick={() => openDrawer(item)}
                                        >
                                            {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                                        </DataGridRow>
                                    )}
                                </DataGridBody>
                            </DataGrid>
                        </div>
                        <PaginationToolbar
                            page={page}
                            pageSize={pageSize}
                            total={total}
                            itemCount={items.length}
                            onPageChange={setPage}
                            onPageSizeChange={(s) => {
                                setPageSize(s);
                                resetPage();
                            }}
                        />
                    </>
                )}
            </Card>

            <ClientDetailDrawer
                client={selected}
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                onCopyId={(clientId) =>
                    dispatchToast(
                        <Toast>
                            <ToastTitle>Client ID {clientId} copied</ToastTitle>
                        </Toast>,
                        { intent: 'success' },
                    )
                }
            />
        </Container>
    );
};
